import { styled } from 'styled-system/jsx'

/* ============================================================
   QuickPass renter pass — "The Pass" credential aesthetic.
   Warm paper, hard ink borders, Space Mono codes, wax stamps,
   perforation + barcode. Cohesive with quickpass.club.
   ============================================================ */

const PageRoot = styled('div', {
  base: {
    minHeight: '100vh',
    backgroundColor: '#f4ecdd',
    backgroundImage:
      'radial-gradient(rgba(26, 19, 48, 0.04) 1px, transparent 1px)',
    backgroundSize: '4px 4px',
    paddingX: '20px',
    paddingY: '40px',
    display: 'flex',
    justifyContent: 'center',
  },
})

const PageInner = styled('div', {
  base: {
    width: '100%',
    maxWidth: '760px',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
})

const BrandRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingX: '2px',
  },
})

const BrandWordmark = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '800',
    color: 'quickpass.ink',
    letterSpacing: '-0.02em',
  },
})

const BrandTagline = styled('span', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
  },
})

/* ---- The pass card (hero) ---------------------------------- */

const HeroCard = styled('section', {
  base: {
    position: 'relative',
    backgroundColor: '#fffcf6',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    borderRadius: 'lg',
    boxShadow: '6px 6px 0 token(colors.quickpass.ink)',
  },
})

const HeroHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    paddingX: '22px',
    paddingY: '15px',
    backgroundColor: 'quickpass.primary',
    color: '#ffffff',
    borderBottom: '2px solid',
    borderColor: 'quickpass.ink',
    borderTopLeftRadius: 'lg',
    borderTopRightRadius: 'lg',
  },
})

const HeroBrand = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '800',
    letterSpacing: '-0.01em',
  },
})

const HeroNo = styled('span', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '12px',
    letterSpacing: '0.08em',
  },
})

const HeroBody = styled('div', {
  base: {
    position: 'relative',
    paddingX: '24px',
    paddingY: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
})

const HeroTop = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
  },
})

const Avatar = styled('div', {
  base: {
    width: '68px',
    height: '68px',
    borderRadius: 'md',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    backgroundColor: 'quickpass.primary',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'display',
    fontSize: '26px',
    fontWeight: '800',
    letterSpacing: '-0.5px',
    flexShrink: '0',
  },
})

const HeroText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    flex: '1',
    minWidth: '0',
  },
})

const Name = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '30px',
    fontWeight: '800',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.03em',
    lineHeight: '1.1',
  },
})

const Headline = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '16px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.45',
  },
})

const BadgeRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
})

const Badge = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingX: '10px',
    paddingY: '5px',
    borderRadius: 'sm',
    border: '1.5px solid #1f8a63',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    backgroundColor: '#e7f7ee',
    color: '#10663a',
  },
})

const InfoBadge = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingX: '10px',
    paddingY: '5px',
    borderRadius: 'sm',
    border: '1.5px solid',
    borderColor: '#ece7fa',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    backgroundColor: '#f7f3ff',
    color: '#5b34d9',
  },
})

const CustomNote = styled('blockquote', {
  base: {
    margin: '0',
    paddingX: '16px',
    paddingY: '13px',
    backgroundColor: '#faf7ff',
    border: '1.5px solid',
    borderColor: '#ece7fa',
    borderLeftWidth: '4px',
    borderLeftColor: 'quickpass.primary',
    borderRadius: 'sm',
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    lineHeight: '1.55',
    whiteSpace: 'pre-wrap',
  },
})

/* ---- Perforation + stub + barcode -------------------------- */

const Perf = styled('div', {
  base: {
    position: 'relative',
    height: '0',
    borderTop: '2px dashed token(colors.quickpass.ink)',
    _before: {
      content: '""',
      position: 'absolute',
      top: '0',
      left: '-13px',
      width: '22px',
      height: '22px',
      borderRadius: '50%',
      backgroundColor: '#f4ecdd',
      border: '2px solid token(colors.quickpass.ink)',
      transform: 'translateY(-50%)',
      clipPath: 'inset(0 0 0 11px)',
    },
    _after: {
      content: '""',
      position: 'absolute',
      top: '0',
      right: '-13px',
      width: '22px',
      height: '22px',
      borderRadius: '50%',
      backgroundColor: '#f4ecdd',
      border: '2px solid token(colors.quickpass.ink)',
      transform: 'translateY(-50%)',
      clipPath: 'inset(0 11px 0 0)',
    },
  },
})

const HeroStub = styled('div', {
  base: {
    paddingX: '24px',
    paddingTop: '16px',
    paddingBottom: '22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const HeroStubRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
  },
})

const Barcode = styled('div', {
  base: {
    height: '40px',
    width: '100%',
    backgroundImage:
      'repeating-linear-gradient(90deg, #1a1330 0 2px, transparent 2px 4px, #1a1330 4px 7px, transparent 7px 9px, #1a1330 9px 10px, transparent 10px 13px, #1a1330 13px 16px, transparent 16px 18px)',
  },
})

/* ---- Section cards ----------------------------------------- */

const SectionCard = styled('section', {
  base: {
    backgroundColor: '#fffcf6',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    borderRadius: 'lg',
    padding: '26px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const SectionTitle = styled('h2', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '800',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.02em',
  },
})

const StatGrid = styled('dl', {
  base: {
    margin: '0',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
})

const StatItem = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
})

const StatLabel = styled('dt', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '10px',
    fontWeight: '700',
    color: 'quickpass.inkSoft',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
})

const StatValue = styled('dd', {
  base: {
    margin: '0',
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '600',
    color: 'quickpass.ink',
  },
})

const AboutGroup = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const AboutSubtitle = styled('h3', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    fontWeight: '700',
    color: 'quickpass.inkSoft',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    margin: '0',
  },
})

const AboutParagraph = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
  },
})

const ContactRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
})

const ContactLine = styled('a', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '9px',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.ink',
    textDecoration: 'none',
    width: 'fit-content',
    _hover: {
      color: '#5b34d9',
    },
  },
})

const FooterText = styled('p', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    letterSpacing: '0.04em',
    color: 'quickpass.inkSoft',
    textAlign: 'center',
    margin: '0',
    lineHeight: '1.6',
  },
})

/* ---- Loading / error states -------------------------------- */

const StateCard = styled('section', {
  base: {
    backgroundColor: '#fffcf6',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    borderRadius: 'lg',
    boxShadow: '6px 6px 0 token(colors.quickpass.ink)',
    padding: '48px 32px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
})

const StateEmoji = styled('span', {
  base: {
    fontSize: '44px',
    lineHeight: '1',
    color: 'quickpass.primary',
  },
})

const StateTitle = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '22px',
    fontWeight: '800',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.02em',
  },
})

const StateBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
    maxWidth: '440px',
    lineHeight: '1.5',
  },
})

/* ---- Verification proofs ----------------------------------- */

const VerifyIntro = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.55',
  },
})

const VerifyList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const VerifyItem = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '18px 20px',
    backgroundColor: '#f4ecdd',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    borderRadius: 'md',
  },
})

const VerifyHead = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
  },
})

const VerifyIcon = styled('span', {
  base: {
    width: '44px',
    height: '44px',
    borderRadius: 'sm',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    backgroundColor: '#a7e8cf',
    color: 'quickpass.ink',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '19px',
    flexShrink: '0',
  },
})

const VerifyHeadText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    flex: '1',
    minWidth: '0',
  },
})

const VerifyTitleRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    flexWrap: 'wrap',
  },
})

const VerifyTitle = styled('h3', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '12px',
    fontWeight: '700',
    color: 'quickpass.inkSoft',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    margin: '0',
  },
})

const VerifyCheck = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingX: '9px',
    paddingY: '4px',
    borderRadius: 'sm',
    border: '1.5px solid #1f8a63',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    backgroundColor: '#e7f7ee',
    color: '#10663a',
    whiteSpace: 'nowrap',
  },
})

const VerifyResult = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: 'quickpass.ink',
  },
})

const VerifyProves = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.55',
  },
})

const VerifyProvider = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    paddingTop: '10px',
    borderTop: '1.5px dashed #ece7fa',
  },
})

const VerifyProviderName = styled('span', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#5b34d9',
  },
})

const VerifyProviderBlurb = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    lineHeight: '1.5',
  },
})

const VerifyMethod = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    letterSpacing: '0.04em',
    color: 'quickpass.inkSoft',
  },
})

const VerifyPrivacy = styled('p', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.5',
  },
})

/* ---- "Don't take our word for it" confirm card ------------- */

const ConfirmCard = styled('section', {
  base: {
    backgroundColor: '#5b34d9',
    color: '#ffffff',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    borderRadius: 'lg',
    boxShadow: '6px 6px 0 token(colors.quickpass.ink)',
    padding: '26px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const ConfirmTitle = styled('h2', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '800',
    color: '#ffffff',
    margin: '0',
    letterSpacing: '-0.02em',
  },
})

const ConfirmLead = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.95)',
    margin: '0',
    lineHeight: '1.6',
    maxWidth: '62ch',
  },
})

const ConfirmSteps = styled('ol', {
  base: {
    listStyle: 'none',
    margin: '2px 0',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '13px',
  },
})

const ConfirmStep = styled('li', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
})

const ConfirmStepNum = styled('span', {
  base: {
    width: '26px',
    height: '26px',
    borderRadius: 'sm',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    backgroundColor: '#ffffff',
    color: 'quickpass.ink',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: '0',
    marginTop: '1px',
  },
})

const ConfirmStepText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
})

const ConfirmStepTitle = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: '#ffffff',
  },
})

const ConfirmStepBody = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: '1.5',
  },
})

const ConfirmButton = styled('a', {
  base: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '9px',
    marginTop: '2px',
    paddingX: '20px',
    paddingY: '12px',
    borderRadius: 'sm',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    backgroundColor: '#ffffff',
    color: 'quickpass.ink',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    _hover: {
      transform: 'translate(-2px, -2px)',
      boxShadow: '4px 4px 0 rgba(0, 0, 0, 0.35)',
    },
  },
})

const ConfirmHint = styled('p', {
  base: {
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    letterSpacing: '0.03em',
    color: 'rgba(255, 255, 255, 0.85)',
    margin: '0',
  },
})

/* ---- Tabs -------------------------------------------------- */

const TabBar = styled('div', {
  base: {
    display: 'flex',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    borderRadius: 'md',
    overflow: 'hidden',
    backgroundColor: '#fffcf6',
    boxShadow: '4px 4px 0 token(colors.quickpass.ink)',
  },
})

const Tab = styled('button', {
  base: {
    flex: '1',
    paddingY: '13px',
    paddingX: '8px',
    border: 'none',
    borderRight: '2px solid token(colors.quickpass.ink)',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
    lineHeight: '1.3',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    _last: { borderRight: 'none' },
    _hover: { backgroundColor: '#f4ecdd' },
  },
  variants: {
    active: {
      true: {
        backgroundColor: 'quickpass.primary',
        color: '#ffffff',
        _hover: { backgroundColor: 'quickpass.primary' },
      },
    },
  },
})

/* ---- "What is QuickPass?" tab ------------------------------ */

const WhatItem = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
  },
})

const WhatIcon = styled('span', {
  base: {
    width: '42px',
    height: '42px',
    borderRadius: 'sm',
    border: '2px solid',
    borderColor: 'quickpass.ink',
    backgroundColor: '#ece7fa',
    color: '#5b34d9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: '0',
  },
})

const WhatText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
})

const WhatTitle = styled('h3', {
  base: {
    fontFamily: 'display',
    fontSize: '16px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const WhatBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.55',
  },
})

const WhatCtaRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 20px',
    marginTop: '4px',
    paddingTop: '16px',
    borderTop: '1.5px dashed #ece7fa',
  },
})

const WhatCta = styled('a', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: "'Space Mono', ui-monospace, 'SFMono-Regular', monospace",
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#5b34d9',
    textDecoration: 'none',
    _hover: {
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
    },
  },
})

export {
  PageRoot,
  PageInner,
  BrandRow,
  BrandWordmark,
  BrandTagline,
  HeroCard,
  HeroHeader,
  HeroBrand,
  HeroNo,
  HeroBody,
  HeroTop,
  Avatar,
  HeroText,
  Name,
  Headline,
  BadgeRow,
  Badge,
  InfoBadge,
  CustomNote,
  Perf,
  HeroStub,
  HeroStubRow,
  Barcode,
  SectionCard,
  SectionTitle,
  StatGrid,
  StatItem,
  StatLabel,
  StatValue,
  AboutGroup,
  AboutSubtitle,
  AboutParagraph,
  ContactRow,
  ContactLine,
  FooterText,
  StateCard,
  StateEmoji,
  StateTitle,
  StateBody,
  VerifyIntro,
  VerifyList,
  VerifyItem,
  VerifyHead,
  VerifyIcon,
  VerifyHeadText,
  VerifyTitleRow,
  VerifyTitle,
  VerifyCheck,
  VerifyResult,
  VerifyProves,
  VerifyProvider,
  VerifyProviderName,
  VerifyProviderBlurb,
  VerifyMethod,
  VerifyPrivacy,
  ConfirmCard,
  ConfirmTitle,
  ConfirmLead,
  ConfirmSteps,
  ConfirmStep,
  ConfirmStepNum,
  ConfirmStepText,
  ConfirmStepTitle,
  ConfirmStepBody,
  ConfirmButton,
  ConfirmHint,
  TabBar,
  Tab,
  WhatItem,
  WhatIcon,
  WhatText,
  WhatTitle,
  WhatBody,
  WhatCtaRow,
  WhatCta,
}
